import {
  FormControlLabel,
  Paper,
  Switch,
  Toolbar,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  makeStyles,
} from '@material-ui/core';
import { DelayedLinearProgress, ResponsiveIconButton } from 'components';
import * as React from 'react';
import { ExternalOrderLog, BoltParsedPayload } from 'interfaces';
import { ExternalOrderService } from 'services';
import { FirebaseService } from 'services/FirebaseService';
import RefreshIcon from '@material-ui/icons/Refresh';
import { BoltStatusActions } from './BoltStatusActions';
import { BoltProviderStatusBar } from './BoltProviderStatusBar';
import { WoltProviderStatusBar } from './WoltProviderStatusBar';
import { WebsiteProviderStatusBar } from './WebsiteProviderStatusBar';
import { PrepTimer } from './PrepTimer';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeaderOk: {
    // Bolt brand green for healthy Bolt orders. Slightly darker than
    // the BOLT pill (#34d186) so the chip still pops on the header.
    // Warn (mapping issues) and Error (parser errors) headers keep
    // their existing orange/red — those still convey signal.
    background: '#1eaf5c',
    color: 'white',
    padding: theme.spacing(1, 2),
  },
  cardHeaderWebsite: {
    // Website purple band: our own storefront, so a mixed queue reads
    // green = Bolt, blue = Wolt, purple = our site.
    background: '#6a1b9a',
    color: 'white',
    padding: theme.spacing(1, 2),
  },
  cardHeaderWolt: {
    // Wolt brand cyan-blue for healthy Wolt orders, so a mixed queue is
    // scannable at a glance: green band = Bolt, blue band = Wolt. Same
    // "blue not green" signal the takeover popup uses.
    background: '#009de0',
    color: 'white',
    padding: theme.spacing(1, 2),
  },
  cardHeaderWarn: {
    background: theme.palette.warning.main,
    color: 'white',
    padding: theme.spacing(1, 2),
  },
  cardHeaderError: {
    background: theme.palette.error.main,
    color: 'white',
    padding: theme.spacing(1, 2),
  },
  itemRow: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  itemName: {
    fontWeight: 600,
  },
  itemNameUnmapped: {
    fontWeight: 600,
    color: theme.palette.error.dark,
  },
  optionRow: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  notes: {
    background: '#fff8e1',
    padding: theme.spacing(0.5, 1),
    marginTop: theme.spacing(0.5),
    fontSize: '0.85rem',
    borderRadius: 4,
  },
  totalRow: {
    fontWeight: 700,
    fontSize: '1.05rem',
  },
  metaRow: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  bannerInfo: {
    background: '#e3f2fd',
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.info.main}`,
  },
  // Unified card header — the colour band IS the only header now.
  // Carries customer name + order ref on the left, type/scheduled
  // chips + total on the right. Bolt-source identity is conveyed by
  // the band colour itself (Bolt green), so the BOLT pill is dropped.
  // Background colour comes from cardHeaderOk / cardHeaderError.
  bagStrip: {
    padding: theme.spacing(1, 2),
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  headerLeft: {
    flex: 1,
    minWidth: 120,
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  customerName: {
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  orderRef: {
    fontSize: '0.8rem',
    opacity: 0.85,
    fontWeight: 600,
  },
  headerTotal: {
    fontSize: '1.05rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: theme.spacing(1),
  },
  chipBolt: {
    background: '#34d186', // Bolt brand green
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipWolt: {
    background: '#00c2e8', // Wolt brand cyan
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipWebsite: {
    background: '#8e24aa', // Website purple, matches the takeover popup
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipPaid: {
    background: '#2e7d32',
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipCash: {
    background: '#c62828',
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipDelivery: {
    background: '#1976d2', // blue
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipPickup: {
    background: '#ef6c00', // orange — visually distinct from delivery
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  chipScheduled: {
    // Blue — visually distinct from delivery's #1976d2 (lighter cyan-blue)
    // so a scheduled DELIVERY order shows both pills without colour clash.
    background: '#0288d1',
    color: 'white',
    fontWeight: 700,
    height: 24,
  },
  courierPanel: {
    background: '#e8f5e9',
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(1),
    borderRadius: 4,
    borderLeft: '4px solid #2e7d32',
    fontSize: '0.85rem',
  },
}));

interface ViewProps {
  shopId: number;
}

// Each log carries its source in externalOrderSource.name ("Bolt" /
// "Wolt"). One helper so every platform branch reads the same way and a
// missing/renamed source degrades to Bolt (the original behaviour).
const platformOf = (log: ExternalOrderLog): 'Bolt' | 'Wolt' | 'Website' => {
  const name = (log.externalOrderSource?.name || '').toLowerCase();
  if (name === 'wolt') return 'Wolt';
  if (name === 'website') return 'Website';
  return 'Bolt';
};

// Card money flag: our own storefront takes cash OR card, and the counter/
// driver must know which before the bag leaves. Card orders only ever reach
// this screen after the payment cleared, so card === already paid.
const isPaidOnline = (parsed: BoltParsedPayload): boolean =>
  ((parsed as any)?.paymentType || '').toLowerCase() === 'card';

export const BoltOrdersPreviewView: React.FC<ViewProps> = ({ shopId }) => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(true);
  const [logs, setLogs] = React.useState<ExternalOrderLog[]>([]);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  // Done orders are hidden by default so staff focus on the active
  // queue. Toggle reveals same-day completed/cancelled/rejected ones
  // for reference (the external_order_logs table is bounded per day,
  // so "show completed" is naturally scoped to today's activity).
  const [showCompleted, setShowCompleted] = React.useState(false);
  // sku -> menuItemId (null when admin hasn't assigned a target yet).
  // Refreshed every fetch so on-the-go mapping is reflected in cards
  // without needing the webhook to re-fire.
  const [skuToMenuItemId, setSkuToMenuItemId] = React.useState<
    Record<string, number | null>
  >({});

  React.useEffect(() => {
    // Clear stale rows + refetch the moment shopId flips so we never
    // briefly show the other outlet's orders. Show the loader for this
    // one — the cards are about to swap.
    setLogs([]);
    fetchLogs();

    // Primary signal: Firebase push. The server bumps a timestamp at
    // external_orders/{platform}/{shopId} whenever a webhook lands or an
    // outbound action goes through. This view is a MIXED Bolt+Wolt queue,
    // so we listen on BOTH platform paths and silently refetch, debounced
    // 1.5s so a burst of events (newOrder + courierDetails in quick
    // succession, or Bolt + Wolt landing together) collapses into one API
    // call. Sub-second new-order visibility, no polling noise.
    const db = FirebaseService.getDatabase();
    const fbRefs = [
      db.ref(`external_orders/bolt/${shopId}`),
      db.ref(`external_orders/wolt/${shopId}`),
      db.ref(`external_orders/website/${shopId}`),
    ];
    let bumpTimer: ReturnType<typeof setTimeout> | null = null;
    // One "first snapshot skipped" flag PER ref — Firebase fires once on
    // attach for each path with the current value, which we don't want to
    // treat as a change.
    const firstSnapshot: Record<number, boolean> = {};
    const handlers = fbRefs.map((ref, i) => {
      firstSnapshot[i] = true;
      const onBump = () => {
        if (firstSnapshot[i]) {
          firstSnapshot[i] = false;
          return;
        }
        if (bumpTimer) clearTimeout(bumpTimer);
        bumpTimer = setTimeout(() => fetchLogs({ silent: true }), 1500);
      };
      ref.on('value', onBump);
      return onBump;
    });

    // Fallback polling: 60s while tab visible. If Firebase drops a
    // bump (network blip, server-side throw swallowed in the helper),
    // the SPA still self-heals within a minute. Silent — no UI flash.
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchLogs({ silent: true });
    }, 60000);

    // Also refetch the moment the user returns to the tab — covers the
    // case where the tab was hidden for hours and the Firebase listener
    // was muted by the browser.
    const onFocus = () => fetchLogs({ silent: true });
    window.addEventListener('focus', onFocus);

    return () => {
      fbRefs.forEach((ref, i) => ref.off('value', handlers[i]));
      if (bumpTimer) clearTimeout(bumpTimer);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  // A Bolt order is "done from the kitchen's perspective" the moment
  // we hand it off. Concretely:
  //   - bolt_order_status = ready_for_pickup → we've marked it ready,
  //     the driver (delivery) or customer (takeaway) is the next actor,
  //     nothing more for us to do
  //   - bolt_order_status = picked_up / delivered → already gone
  //   - internal status terminal cancel/reject → won't be made, drop off
  const DONE_INTERNAL_STATUSES = new Set([
    'rejected',
    'cancel_received',
    'cancelled',
  ]);
  const isDone = (log: ExternalOrderLog) => {
    const bolt = (log.boltOrderStatus || '').toLowerCase();
    const internal = (log.status || '').toLowerCase();
    if (DONE_INTERNAL_STATUSES.has(internal)) return true;
    // Terminal Bolt/Wolt statuses that end our involvement, whatever the
    // internal status still says. A decline sets bolt_order_status to
    // 'rejected' but leaves the internal status as parsed/needs_mapping,
    // so without 'rejected' here a declined order lingers in the active
    // queue instead of dropping to Completed. A cancel (arriving after
    // acceptance) flips it to 'cancelled' the same way.
    if (bolt === 'cancelled' || bolt === 'rejected') return true;

    // Order type decides who closes the order out. Handle both key casings
    // — parsed_payload may or may not be camelised depending on the
    // response path.
    const pp: any = log.parsedPayload || {};
    const orderType = (pp.order_type || pp.orderType || '').toLowerCase();
    const isDelivery = orderType === 'delivery';
    // Explicit customer-collection. Deliberately not "!isDelivery": Wolt
    // orders parsed before 2026-07-20 froze order_type as "instant" and are
    // overwhelmingly home deliveries, so they must keep the delivery rule.
    const isCollection = orderType === 'takeaway' || orderType === 'eatin';

    if (platformOf(log) === 'Website') {
      // Our own storefront: one hand-off closes it, whoever collects.
      // Marking picked up flips a delivery customer's status page to
      // "on its way" and drops the card off the active board.
      return bolt === 'picked_up';
    }

    if (platformOf(log) === 'Wolt') {
      // Wolt-courier home delivery: Mark Ready is our last action, their
      // courier closes it. TAKEAWAY: we still owe a Mark Delivered when the
      // customer collects, so it stays on the board until then.
      if (isCollection) {
        return ['picked_up', 'delivered'].includes(bolt);
      }
      return ['ready_for_pickup', 'picked_up', 'delivered'].includes(bolt);
    }

    // For Bolt DELIVERY, ready_for_pickup is our last action — Bolt's
    // courier handles pickup + delivery. For Bolt TAKEAWAY/PICKUP we still
    // owe a "Mark Picked Up" when the customer collects (that's what closes
    // the order on their app), so the card must stay visible until then.
    if (isDelivery) {
      return ['ready_for_pickup', 'picked_up', 'delivered'].includes(bolt);
    }
    return ['picked_up', 'delivered'].includes(bolt);
  };

  const parsedLogs = logs.filter(
    (log) => log.parsedPayload && typeof log.parsedPayload === 'object',
  );
  const activeCount = parsedLogs.filter((l) => !isDone(l)).length;
  const completedCount = parsedLogs.length - activeCount;
  // Strict either/or: the toggle used to show EVERYTHING when on, which
  // parked live orders (with a working Mark Ready button) among finished
  // ones and read as "this completed order still needs marking"
  // (2026-07-15). Now it is completed-only vs active-only.
  const previewable = showCompleted
    ? parsedLogs.filter((l) => isDone(l))
    : parsedLogs.filter((l) => !isDone(l));

  /**
   * Determine an item's CURRENT mapping status. Bolt's webhook landed
   * minutes ago and parsed_payload froze the state then. Admin may have
   * since mapped the SKU in the Menu Mappings tab. We re-resolve here
   * so the preview reflects the live mappings table.
   */
  const liveMappingStatus = (item: {
    sku: string | null;
    mappingStatus: string;
  }): 'mapped' | 'unmapped' | 'invalid' | 'error' => {
    if (!item.sku) return item.mappingStatus as any;
    if (item.sku in skuToMenuItemId) {
      return skuToMenuItemId[item.sku] !== null ? 'mapped' : 'unmapped';
    }
    return item.mappingStatus as any;
  };

  const liveOrderMappingStatus = (parsed: BoltParsedPayload) => {
    const all = parsed.items || [];
    if (all.length === 0) return 'needs_mapping';
    const everyMapped = all.every((it) => liveMappingStatus(it) === 'mapped');
    return everyMapped ? 'fully_mapped' : 'needs_mapping';
  };

  const formatMoney = (
    value: number | null | undefined,
    currency?: string | null,
  ) => {
    if (value === null || value === undefined) return '—';
    const symbol = currency === 'eur' ? '€' : currency || '';
    return `${symbol}${value.toFixed(2)}`;
  };

  /**
   * Render a price with an optional "was €X.XX (-N%)" discount tag
   * inline next to it. Bolt's price object is split by the parser into
   * `value` + an optional `discount = {original_value, discount_percent, savings}`.
   */
  const formatMoneyWithDiscount = (
    value: number | null | undefined,
    currency: string | null | undefined,
    // ApiClient deep-camelises responses, so a discount block arrives as
    // {originalValue, discountPercent, savings}. The raw parsed_payload is
    // snake_case though, and which one we get depends on the response path
    // — so accept BOTH. Reading only snake_case silently rendered
    // "(was —)" on every discounted order (2026-07-20).
    discount?: {
      originalValue?: number | null;
      discountPercent?: number | null;
      savings?: number | null;
      original_value?: number | null;
      discount_percent?: number | null;
    } | null,
  ) => {
    const main = formatMoney(value, currency);
    if (!discount) return <span>{main}</span>;
    const orig = discount.originalValue ?? discount.original_value;
    const pct = discount.discountPercent ?? discount.discount_percent;
    // No usable original price → show the plain price rather than a
    // meaningless "was —" tag.
    if (orig === null || orig === undefined) return <span>{main}</span>;
    return (
      <span>
        {main}{' '}
        <span style={{ color: '#1565c0', fontSize: '0.75em' }}>
          (was {formatMoney(orig, currency)}
          {pct != null ? `, -${Math.round(pct)}%` : ''})
        </span>
      </span>
    );
  };

  const formatDt = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  const headerClassFor = (
    parsed: BoltParsedPayload,
    platform: 'Bolt' | 'Wolt' | 'Website',
  ) => {
    // Parser errors keep the red header — that's a real "we can't read this
    // order" alarm, on either platform. We dropped the orange
    // "needs_mapping" header: each unmapped item already carries its own
    // red title + "needs mapping: sku-X" chip, so the header colour was
    // duplicate signal. Otherwise the band colour signals the source:
    // green = Bolt, blue = Wolt.
    if (parsed.parserErrors && parsed.parserErrors.length > 0)
      return classes.cardHeaderError;
    if (platform === 'Wolt') return classes.cardHeaderWolt;
    if (platform === 'Website') return classes.cardHeaderWebsite;
    return classes.cardHeaderOk;
  };

  // Right-side controls of the single consolidated header. Refresh,
  // active/completed counter, and the show-completed toggle all sit
  // inside the BoltProviderStatusBar row now instead of getting their
  // own Paper below. The previous "Bolt status only..." blue banner is
  // gone entirely.
  const headerRight = (
    <React.Fragment>
      <ResponsiveIconButton
        onClick={() => fetchLogs()}
        color="primary"
        icon={RefreshIcon}
      >
        Refresh
      </ResponsiveIconButton>
      <Typography variant="body2" color="textSecondary">
        {activeCount} active
        {completedCount > 0 && ` · ${completedCount} completed`}
      </Typography>
      {completedCount > 0 && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showCompleted}
              onChange={(_e, v) => setShowCompleted(v)}
              color="primary"
            />
          }
          label="Show completed"
        />
      )}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <BoltProviderStatusBar shopId={shopId} rightSlot={headerRight} />
      <WoltProviderStatusBar shopId={shopId} />
      {/* Our own storefront's manual switch - shop 2 only until Street
          Food joins the website ordering. */}
      {shopId === 2 && <WebsiteProviderStatusBar />}

      {fetchError && (
        <Paper
          style={{
            padding: 12,
            marginBottom: 8,
            background: '#ffebee',
            borderLeft: '4px solid #c62828',
          }}
        >
          <Typography variant="body2" color="error">
            Failed to load orders: {fetchError}
          </Typography>
        </Paper>
      )}

      {loading && <DelayedLinearProgress />}

      {!loading && previewable.length === 0 && (
        <Paper style={{ padding: 24, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No live Bolt or Wolt orders right now. New orders appear here
            automatically.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {previewable.map((log) => {
          const parsed = log.parsedPayload as BoltParsedPayload;
          const platform = platformOf(log);
          return (
            <Grid item xs={12} sm={6} md={4} key={log.id}>
              <Card className={classes.card}>
                {/*
                  Unified header: platform pill + customer name + order ref
                  on the left, type/scheduled chips + total on the right.
                  The band colour (green = Bolt, blue = Wolt, red on parser
                  errors) signals the source; the pill spells it out so a
                  mixed queue is unambiguous.
                */}
                <div
                  className={`${classes.bagStrip} ${headerClassFor(
                    parsed,
                    platform,
                  )}`}
                >
                  <div className={classes.headerLeft}>
                    <Chip
                      label={platform.toUpperCase()}
                      size="small"
                      className={
                        platform === 'Wolt'
                          ? classes.chipWolt
                          : platform === 'Website'
                          ? classes.chipWebsite
                          : classes.chipBolt
                      }
                    />
                    <span
                      className={classes.customerName}
                      title={`${platform} id: ${log.externalOrderId}`}
                    >
                      {/*
                        Wolt shows the order reference as the primary label.
                        That number is what the customer and courier match the
                        bag by. Replaced the customer name 2026-08-13 (name not
                        shown for Wolt). Bolt keeps the customer name.
                      */}
                      {platform === 'Wolt'
                        ? `#${parsed.orderReference || '—'}`
                        : parsed.customer?.partialName || '— no name —'}
                    </span>
                  </div>
                  {platform === 'Website' ? (
                    isPaidOnline(parsed) ? (
                      <Chip label="PAID" size="small" className={classes.chipPaid} />
                    ) : (
                      <Chip
                        label={`CASH ${formatMoney(
                          parsed.totalPrice,
                          parsed.totalCurrency,
                        )}`}
                        size="small"
                        className={classes.chipCash}
                      />
                    )
                  ) : null}
                  {parsed.orderType === 'takeaway' ? (
                    <Chip
                      label="PICKUP"
                      size="small"
                      className={classes.chipPickup}
                    />
                  ) : parsed.orderType === 'delivery' ? (
                    <Chip
                      label="DELIVERY"
                      size="small"
                      className={classes.chipDelivery}
                    />
                  ) : (
                    <Chip
                      label={(parsed.orderType || 'UNKNOWN').toUpperCase()}
                      size="small"
                    />
                  )}
                  {(() => {
                    if (!parsed.dueDatetime) return null;
                    const due = new Date(parsed.dueDatetime);
                    if (isNaN(due.getTime())) return null;
                    const minutesAhead =
                      (due.getTime() - Date.now()) / 60000;
                    if (minutesAhead > 30) {
                      // Always surface WHEN — "SCHEDULED" alone left staff
                      // guessing (2026-07-15). PrepTimer shows the matching
                      // kitchen-start / countdown next to this chip.
                      const hhmm = (d: Date) =>
                        `${d.getHours().toString().padStart(2, '0')}:${d
                          .getMinutes()
                          .toString()
                          .padStart(2, '0')}`;
                      return (
                        <Chip
                          label={`SCHEDULED · DUE ${hhmm(due)}`}
                          size="small"
                          className={classes.chipScheduled}
                        />
                      );
                    }
                    return null;
                  })()}
                  <span className={classes.headerTotal}>
                    {formatMoneyWithDiscount(
                      parsed.totalPrice,
                      parsed.totalCurrency,
                      parsed.totalDiscount,
                    )}
                  </span>
                </div>
                <CardContent style={{ flex: 1 }}>
                  <div style={{ marginBottom: 8 }}>
                    <PrepTimer log={log} />
                  </div>

                  {/*
                    Who it is and where it goes. Bolt and Wolt keep their
                    couriers' details to themselves, so these fields are
                    usually empty there; on a Website order WE deliver, so
                    staff need the phone and the address without opening the
                    order (Lucy 2026-09-06).
                  */}
                  {(parsed.customer?.partialPhone ||
                    parsed.customer?.address) && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 12,
                        alignItems: 'baseline',
                        margin: '4px 0 8px',
                        fontSize: '0.85rem',
                        color: '#37474f',
                      }}
                    >
                      {parsed.customer?.partialPhone ? (
                        <a
                          href={`tel:${parsed.customer.partialPhone}`}
                          style={{ fontWeight: 700, color: '#1565c0' }}
                        >
                          ☏ {parsed.customer.partialPhone}
                        </a>
                      ) : null}
                      {parsed.customer?.address ? (
                        <span>
                          <span role="img" aria-label="address">
                            📍
                          </span>{' '}
                          {parsed.customer.address}
                        </span>
                      ) : null}
                    </div>
                  )}

                  {parsed.orderNote ? (
                    <div
                      style={{
                        background: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 4,
                        padding: '6px 10px',
                        margin: '4px 0 8px',
                        fontWeight: 600,
                        color: '#795548',
                      }}
                    >
                      ✎ Customer note: {parsed.orderNote}
                    </div>
                  ) : null}

                  <Divider style={{ margin: '8px 0' }} />

                  {parsed.items.map((item, idx) => {
                    const liveStatus = liveMappingStatus(item);
                    const unmapped = liveStatus !== 'mapped';
                    return (
                      <div key={idx} className={classes.itemRow}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span
                            className={
                              unmapped
                                ? classes.itemNameUnmapped
                                : classes.itemName
                            }
                          >
                            {item.qty}× {item.name || '(no name)'}
                          </span>
                          <span>
                            {formatMoneyWithDiscount(
                              item.totalPrice,
                              parsed.totalCurrency,
                              item.totalDiscount,
                            )}
                          </span>
                        </div>
                        {unmapped && (
                          <Chip
                            label={`needs mapping: ${item.sku || '(no sku)'}`}
                            size="small"
                            color="secondary"
                            style={{ marginTop: 2 }}
                          />
                        )}
                        {item.options &&
                          item.options.map((opt, oi) => (
                            <div key={oi} className={classes.optionRow}>
                              + {opt.qty}× {opt.name || '(no name)'} —{' '}
                              {formatMoney(
                                opt.totalPrice,
                                parsed.totalCurrency,
                              )}
                            </div>
                          ))}
                        {item.userNote && (
                          <div className={classes.notes}>
                            Note: {item.userNote}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Divider style={{ margin: '8px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    className={classes.totalRow}
                  >
                    <span>Total</span>
                    <span>
                      {formatMoneyWithDiscount(
                        parsed.totalPrice,
                        parsed.totalCurrency,
                        parsed.totalDiscount,
                      )}
                    </span>
                  </div>

                  {log.courierDetails && (() => {
                    let courier: any = null;
                    try {
                      const parsedCD =
                        typeof log.courierDetails === 'string'
                          ? JSON.parse(log.courierDetails)
                          : log.courierDetails;
                      // Bolt nests the courier object; tolerate both
                      // {courier:{...}} and top-level shape.
                      courier = parsedCD?.courier || parsedCD;
                    } catch (_e) {
                      courier = null;
                    }
                    if (!courier) return null;
                    return (
                      <div className={classes.courierPanel}>
                        <Typography
                          variant="caption"
                          style={{ fontWeight: 700, display: 'block' }}
                        >
                          Courier on the way
                        </Typography>
                        {courier.name && (
                          <div>
                            <strong>Name:</strong> {courier.name}
                          </div>
                        )}
                        {courier.phone && (
                          <div>
                            <strong>Phone:</strong> {courier.phone}
                          </div>
                        )}
                        {courier.vehicle && (
                          <div>
                            <strong>Vehicle:</strong> {courier.vehicle}
                          </div>
                        )}
                        {log.courierDetailsReceivedAt && (
                          <div className={classes.metaRow}>
                            assigned {formatDt(log.courierDetailsReceivedAt as any)}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {parsed.parserErrors && parsed.parserErrors.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Typography variant="caption" color="error">
                        Parser errors:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {parsed.parserErrors.map((e, i) => (
                          <li key={i}>
                            <Typography variant="caption" color="error">
                              {e}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <BoltStatusActions
                    log={log}
                    platform={platform}
                    onChanged={fetchLogs}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </React.Fragment>
  );

  async function fetchLogs(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setFetchError(null);
    try {
      // Logs + current mappings in parallel. No platform filter — this is
      // the unified queue, so we pull BOTH Bolt and Wolt orders (and both
      // platforms' mappings) for the shop. The mappings give us the
      // freshest sku->menuItemId so the preview reflects any mapping the
      // admin just made, without re-receiving the webhook. Our PKG SKUs are
      // shared across platforms and resolve to the same menu item, so a
      // single sku->menuItemId lookup is correct for both.
      const [logsResp, mappingsResp] = await Promise.all([
        ExternalOrderService.getLogs({ shopId }),
        ExternalOrderService.getMappings({ shopId }),
      ]);
      // This view is the accept/manage queue. GloriaFood auto-fires on
      // arrival and is handled by its own review popup, so it never
      // surfaces here (2026-07-27). Website (our own storefront) DOES:
      // Lucy wants it queued and actioned exactly like a Bolt/Wolt order
      // (2026-09-05), and its card speaks the /website endpoints.
      const marketplaceLogs = logsResp.filter(
        (l) => (l.externalOrderSource?.name || '') !== 'GloriaFood',
      );
      // Skip the React re-render if the data is byte-for-byte identical
      // to what's already on screen. Background polls usually return the
      // same data, so this stops every 10-second tick from re-rendering
      // every card on the page for nothing.
      setLogs((prev) =>
        JSON.stringify(prev) === JSON.stringify(marketplaceLogs)
          ? prev
          : marketplaceLogs,
      );
      const lookup: Record<string, number | null> = {};
      (mappingsResp || []).forEach((m) => {
        if (m.externalItemId) {
          // A set-menu mapping (Special Set Menu, Combo Deal) carries
          // set_menu_id and leaves menu_item_id null. Reading only
          // menuItemId flagged those lines "needs mapping" on the card even
          // though the injector resolves them fine (2026-09-06). Only the
          // null-vs-not matters here, so either id answers the question.
          lookup[m.externalItemId] = m.menuItemId ?? m.setMenuId ?? null;
        }
      });
      setSkuToMenuItemId((prev) =>
        JSON.stringify(prev) === JSON.stringify(lookup) ? prev : lookup,
      );
    } catch (e) {
      if (!silent) {
        setLogs([]);
        setSkuToMenuItemId({});
      }
      setFetchError((e as Error)?.message || 'Unknown error fetching logs');
    } finally {
      if (!silent) setLoading(false);
    }
  }
};
